/**
 * Inspection Flow Timing Logger
 *
 * Coarse, whole-inspection timing that sits alongside the deep OD-specific
 * captureTimingLogger.ts. Where captureTimingLogger times a single container/
 * label capture, this logger times the four inspection phases plus review and
 * submission.
 *
 * Phases (mirrors the category mapping used by austinInspectionCheckpointsPage.tsx):
 *  - cage_walk:      the open-ended container loop (IterateStep: read_barcode +
 *                     detect_object sub-steps). Mirrors mode==="ai" && iterable.
 *  - area_scan:      manual_capture_multiple steps. Mirrors mode==="ai" && !iterable.
 *  - photo_evidence: question_id steps whose question.mode === "image".
 *  - manual:         question_id steps whose question.mode === "manual".
 *
 * Plus two boundaries outside the WorkflowExecutor state machine:
 *  - review:     from when review results finish loading (no longer pending
 *                AI analysis, so the inspector can actually read/override them)
 *                to the Submit button being clicked.
 *  - submission: the Austin/SafetyGenie submit API call itself.
 *
 * inspectionTotalMs spans from the "Start Inspection" button click to the
 * submission call resolving.
 *
 * RESUMED DRAFTS ARE NOT PHASE-TIMED. A resumed inspection can restart
 * mid-phase (e.g. finished 2 of 5 containers, then left and resumed on the
 * 3rd), and the time already spent before leaving is unrecoverable
 * client-side — so a segment measured from the resume point would be a
 * misleading partial. Phase segments (cage_walk / area_scan / photo_evidence
 * / manual) are therefore only produced for a fresh "Start Inspection" run
 * (startInspection()). A resumed draft still gets review + submission timing
 * (via identifyInspection() on the review page), flagged resumed=true, but
 * no phase segments and no inspectionTotalMs.
 *
 * ============================================
 * EMITTED AS EACH SEGMENT CLOSES, NOT DEFERRED TO SUBMISSION
 * ============================================
 * A phase can be revisited (e.g. cage_walk once per container), so each visit
 * closes out as its own "segment" log+metric — mirroring how captureTimingLogger
 * logs every capture and barcodeScanTimingLogger logs every scan attempt,
 * rather than waiting for one final rollup. This means cage_walk / area_scan /
 * photo_evidence / manual times are already durably logged as the inspector
 * moves between phases, even if they never reach or click Submit. Only the
 * inspectionTotalMs summary line genuinely requires submission to exist, since
 * "the entire inspection ending in submission" has no meaning otherwise.
 *
 * ============================================
 * IMPLEMENTATION NOTE — module-singleton, not Redux
 * ============================================
 * State lives in this module's singleton instance (same pattern as
 * captureTimingLogger.ts), not Redux. It survives client-side route
 * navigation within the SPA (checkpoints -> execute -> review -> submitted)
 * but resets on a full page reload, same limitation as every other FE-only
 * timing signal in this codebase. A segment still in progress when the tab
 * closes (never closed out) is not logged, same as an in-progress capture in
 * captureTimingLogger.
 *
 * ============================================
 * CLOUDWATCH METRICS (namespace: SafetyGenie/ClientInspection)
 * ============================================
 * Published via EMF.
 *  - PhaseSegmentMs   dimensions: phase (cage_walk|area_scan|photo_evidence|manual|review),
 *                     and — for area_scan/photo_evidence/manual — additionally
 *                     phase x questionId (questionId is a bounded ~40-value
 *                     set, safe as a dimension). Each area_scan question is
 *                     its own step, so it carries a single questionId just
 *                     like photo_evidence/manual. cage_walk/review have no
 *                     questionId and are emitted phase-only. Only emitted for
 *                     a fresh-start inspection (never a resume).
 *
 *                     HOW LONG DID A PHASE TAKE: use the SUM statistic, not
 *                     Average. Each segment is one raw duration; a phase runs
 *                     once per inspection, so Sum(PhaseSegmentMs) grouped by
 *                     phase = total time that phase took. Average would give
 *                     "typical time per question/visit", which is a different
 *                     question. For per-question totals, Sum grouped by
 *                     phase x questionId.
 *  - SubmissionMs     no dimension, one value per inspection (fresh or resumed)
 *  - InspectionTotalMs  no dimension, one value per submitted inspection —
 *                     only emitted for a fresh start, never for a resume.
 *
 * ============================================
 * QUERY IN CLOUDWATCH LOGS INSIGHTS (per-run detail):
 * ============================================
 *   fields katalLogger.message
 *   | filter katalLogger.message like /\[InspectionFlowTiming\]/
 *   | sort @timestamp desc
 * To scope to one inspection:
 *   | filter katalLogger.message like /"inspectionId":"<id>"/
 */

import { cloudWatchLogger } from "./CloudWatchLoggerService";

export type InspectionPhase = "cage_walk" | "area_scan" | "photo_evidence" | "manual";
/** Phase dimension values used on segment logs/metrics — the four timed phases plus review. */
type SegmentPhase = InspectionPhase | "review";

class InspectionFlowTimingLogger {
  private inspectionId: string | null = null;
  private siteCode: string | null = null;
  private inspectionStartTime: number | null = null;
  /** True when this run was seeded via resumeInspection() rather than startInspection(). */
  private isResumed = false;

  private currentPhase: InspectionPhase | null = null;
  private currentQuestionId: string | null = null;
  private currentPhaseStartTime: number | null = null;

  private reviewStartTime: number | null = null;
  private submissionStartTime: number | null = null;

  /**
   * Start timing a fresh inspection, from the "Start Inspection" button
   * click. Resets any state left over from a previous inspection in this
   * session. Not called for a resumed draft — a resume has no reliable
   * "start" moment to anchor inspectionTotalMs to. Review and submission
   * timing still work for a resumed draft (see markReviewStart /
   * markSubmissionStart), just without inspectionTotalMs.
   */
  startInspection(inspectionId: string, siteCode?: string | null): void {
    this.inspectionId = inspectionId;
    this.siteCode = siteCode ?? null;
    this.inspectionStartTime = Date.now();
    this.isResumed = false;
    this.currentPhase = null;
    this.currentPhaseStartTime = null;
    this.reviewStartTime = null;
    this.submissionStartTime = null;
  }

  /**
   * Identify the inspection for review/submission timing when
   * startInspection() was not called (a resumed draft — resumed drafts are
   * not phase-timed, but still get review/submission timing once they reach
   * the review page). No-op if startInspection() already set an id (don't
   * overwrite a real fresh start with a resume-time id/site). Marks the run
   * as resumed so it never produces phase segments or an inspectionTotalMs.
   */
  identifyInspection(inspectionId: string, siteCode?: string | null): void {
    if (this.inspectionId == null) {
      this.inspectionId = inspectionId;
      this.siteCode = siteCode ?? null;
      this.isResumed = true;
    }
  }

  /**
   * Set the currently-active step: its phase, plus the question id it maps to
   * (null when the phase has no single question — cage_walk, or a step that
   * isn't one of the four timed phases). Closes out and logs+emits the
   * previous segment before switching. A new segment starts whenever EITHER
   * the phase OR the question id changes, so two consecutive photo_evidence /
   * manual questions produce two separate per-question segments rather than
   * one merged block. Safe to call repeatedly with the same (phase,
   * questionId) pair (no-op) or with (null, null).
   */
  setActiveStep(phase: InspectionPhase | null, questionId: string | null): void {
    // Phase timing is only done for a fresh "Start Inspection" run
    // (startInspection() sets inspectionStartTime). A resumed draft
    // (identifyInspection(), inspectionStartTime === null) is never
    // phase-timed — see the RESUMED DRAFTS note at the top of this file.
    if (this.inspectionStartTime == null) {
      return;
    }
    if (phase === this.currentPhase && questionId === this.currentQuestionId) {
      return;
    }
    this.closeCurrentSegment();
    this.currentPhase = phase;
    this.currentQuestionId = questionId;
    this.currentPhaseStartTime = phase ? Date.now() : null;
  }

  /**
   * Closes out whichever segment is currently open (if any) and logs+emits it.
   * This is what makes cage_walk / area_scan / photo_evidence / manual
   * durably visible in CloudWatch as soon as the inspector moves on — no
   * submission required.
   */
  private closeCurrentSegment(): void {
    if (this.currentPhase && this.currentPhaseStartTime != null) {
      this.emitSegment(this.currentPhase, this.currentQuestionId, Date.now() - this.currentPhaseStartTime);
    }
  }

  /** Mark the start of the review phase (results finished loading, no longer pending). */
  markReviewStart(): void {
    if (this.reviewStartTime == null) {
      this.reviewStartTime = Date.now();
    }
  }

  /**
   * Mark the start of the Austin/SafetyGenie submission call. Closes out and
   * logs+emits the review segment (durable even if the inspector never
   * successfully submits afterward, e.g. a network failure).
   */
  markSubmissionStart(): void {
    if (this.reviewStartTime != null) {
      this.emitSegment("review", null, Date.now() - this.reviewStartTime);
      this.reviewStartTime = null;
    }
    this.submissionStartTime = Date.now();
  }

  /**
   * Logs a single per-run line and emits the EMF metric for one completed
   * segment. Raw per-segment value; CloudWatch computes Average/p50/p90 across
   * segments for the aggregate view. inspectionId/siteCode stay log-only
   * (unbounded cardinality), same rule as every other logger here.
   *
   * questionId is a metric dimension when present (area_scan / photo_evidence /
   * manual) — the question bank is a small bounded set (~40), so it's safe as
   * a dimension, unlike unbounded ids. When null (cage_walk / review), the
   * metric is emitted with the phase dimension only; the two dimension sets
   * live in one PutMetricData so the metric is queryable both by phase alone
   * and by phase x questionId.
   */
  private emitSegment(phase: SegmentPhase, questionId: string | null, segmentMs: number): void {
    cloudWatchLogger.info(
      `[InspectionFlowTiming] ${JSON.stringify({
        metric: "InspectionFlowTiming",
        level: "segment",
        siteCode: this.siteCode,
        inspectionId: this.inspectionId,
        phase,
        questionId,
        segmentMs,
      })}`
    );

    // Always publish the phase-only rollup; additionally publish a
    // phase x questionId variant when we have a question id, so per-question
    // aggregates are available without breaking the phase-level view.
    const dimensionSets: string[][] = questionId ? [["phase"], ["phase", "questionId"]] : [["phase"]];

    cloudWatchLogger.emitMetric({
      _aws: {
        Timestamp: Date.now(),
        CloudWatchMetrics: [
          {
            Namespace: "SafetyGenie/ClientInspection",
            Dimensions: dimensionSets,
            Metrics: [{ Name: "PhaseSegmentMs", Unit: "Milliseconds" }],
          },
        ],
      },
      phase,
      ...(questionId ? { questionId } : {}),
      PhaseSegmentMs: segmentMs,
    });
  }

  /**
   * Record a completed inspection: close out any still-open phase (as its
   * own segment, per closeCurrentPhase) and the submission timer, log +
   * emit the inspection-level summary, then reset. No-op if no inspection
   * was ever identified (startInspection() / identifyInspection()) — nothing
   * to report. inspectionTotalMs is only included for a fresh start
   * (startInspection()), never for a resume.
   */
  recordInspectionComplete(): void {
    if (this.inspectionId == null) {
      return;
    }
    this.closeCurrentSegment();
    this.currentPhase = null;
    this.currentQuestionId = null;
    this.currentPhaseStartTime = null;

    const submissionMs = this.submissionStartTime != null ? Date.now() - this.submissionStartTime : null;
    const inspectionTotalMs = this.inspectionStartTime != null ? Date.now() - this.inspectionStartTime : null;

    cloudWatchLogger.info(
      `[InspectionFlowTiming] ${JSON.stringify({
        metric: "InspectionFlowTiming",
        level: "inspection",
        siteCode: this.siteCode,
        inspectionId: this.inspectionId,
        resumed: this.isResumed,
        submissionMs,
        inspectionTotalMs,
      })}`
    );

    const metricValues: Record<string, number> = {};
    if (submissionMs != null) metricValues.SubmissionMs = submissionMs;
    if (inspectionTotalMs != null) metricValues.InspectionTotalMs = inspectionTotalMs;

    if (Object.keys(metricValues).length > 0) {
      cloudWatchLogger.emitMetric({
        _aws: {
          Timestamp: Date.now(),
          CloudWatchMetrics: [
            {
              Namespace: "SafetyGenie/ClientInspection",
              Dimensions: [[]],
              Metrics: Object.keys(metricValues).map((name) => ({ Name: name, Unit: "Milliseconds" })),
            },
          ],
        },
        ...metricValues,
      });
    }

    this.reset();
  }

  /**
   * Discard in-progress timing state without logging (e.g. inspector left
   * mid-phase before it closed). Any already-closed segments logged by
   * emitSegment() before this point are unaffected — they were already sent.
   */
  cancel(): void {
    this.reset();
  }

  private reset(): void {
    this.inspectionId = null;
    this.siteCode = null;
    this.inspectionStartTime = null;
    this.isResumed = false;
    this.currentPhase = null;
    this.currentQuestionId = null;
    this.currentPhaseStartTime = null;
    this.reviewStartTime = null;
    this.submissionStartTime = null;
  }
}

export const inspectionFlowTimingLogger = new InspectionFlowTimingLogger();

export default inspectionFlowTimingLogger;
